import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VerifiedBadges } from "../components/ErrandCard";
import { ServiceCard } from "../components/ServiceCard";
import { EmergencyContacts } from "../components/EmergencyContacts";
import { StarRating } from "../lib/constants";
import { Button } from "../components/ui/button";
import { ShieldCheck, ShieldAlert, Phone, IdCard, Loader2, MapPin } from "lucide-react";

export default function Profile() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);
  const [busy, setBusy] = useState(false);
  const isMe = user?.id === id;

  const load = async () => {
    const [u, s] = await Promise.all([
      api.get(`/users/${id}`),
      api.get(`/services/by-runner/${id}`).catch(() => ({ data: [] })),
    ]);
    setData(u.data);
    setServices(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const verify = async (type) => {
    setBusy(true);
    try {
      await api.post("/profile/verify", { type });
      toast.success(`${type === "id" ? "ID" : "Phone"} verified!`);
      await refreshUser();
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-20 text-[#6B7280]"><Loader2 className="animate-spin" /></div>;
  const u = data.user;

  return (
    <div className="max-w-3xl mx-auto px-5 py-8">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 sm:p-8 mb-6">
        <div className="flex items-start gap-4 mb-5">
          <span className="w-20 h-20 rounded-3xl bg-primary/15 text-primary flex items-center justify-center font-heading font-black text-3xl shrink-0">
            {u.display_name?.[0]?.toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-[#1A1D20]">{u.display_name}</h1>
            {u.location && <p className="text-sm text-[#6B7280] flex items-center gap-1 mt-0.5"><MapPin size={14} /> {u.location}</p>}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <StarRating value={u.rating_avg} />
              <span className="text-sm text-[#6B7280]">{u.rating_avg?.toFixed(1)} ({u.rating_count} reviews)</span>
              {typeof u.upheld_reports === "number" && (
                u.upheld_reports === 0 ? (
                  <span data-testid="trust-standing" className="inline-flex items-center gap-1 text-xs font-bold bg-[#E7F6F0] text-[#2F8F6E] rounded-full px-2.5 py-1">
                    <ShieldCheck size={12} /> Good standing · 0 upheld reports
                  </span>
                ) : (
                  <span data-testid="trust-standing" className="inline-flex items-center gap-1 text-xs font-bold bg-[#FFF7ED] text-[#9A6212] rounded-full px-2.5 py-1">
                    <ShieldAlert size={12} /> {u.upheld_reports} upheld report{u.upheld_reports === 1 ? "" : "s"}
                  </span>
                )
              )}
            </div>
          </div>
        </div>

        <div className="mb-5"><VerifiedBadges user={u} /></div>

        <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-100">
          <div>
            <p className="text-xs font-semibold uppercase text-[#9CA3AF]">Completed</p>
            <p className="font-heading font-black text-2xl text-[#1A1D20]">{u.errands_completed}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-[#9CA3AF]">Role</p>
            <p className="font-heading font-black text-2xl text-[#1A1D20] capitalize">{u.role}</p>
          </div>
        </div>

        {isMe && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="font-heading font-extrabold text-[#1A1D20] mb-3 flex items-center gap-2"><ShieldCheck size={18} className="text-[#66C2A5]" /> Verification</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Button data-testid="verify-phone-btn" onClick={() => verify("phone")} disabled={busy || u.phone_verified} variant="outline"
                className="rounded-2xl h-12 font-semibold border-2 justify-start">
                <Phone size={16} className="mr-2" /> {u.phone_verified ? "Phone verified ✓" : "Verify phone"}
              </Button>
              <Button data-testid="verify-id-btn" onClick={() => verify("id")} disabled={busy || u.id_verified} variant="outline"
                className="rounded-2xl h-12 font-semibold border-2 justify-start">
                <IdCard size={16} className="mr-2" /> {u.id_verified ? "ID verified ✓" : "Verify ID"}
              </Button>
            </div>
            <p className="text-xs text-[#9CA3AF] mt-2">Verifying boosts your trust score and helps neighbors feel safe.</p>
          </div>
        )}
      </motion.div>

      {isMe && <EmergencyContacts />}

      {services.length > 0 && (
        <div className="mb-8" data-testid="profile-services">
          <h2 className="font-heading font-extrabold text-xl text-[#1A1D20] mb-3">Services offered</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {services.map((s, i) => <ServiceCard key={s.id} service={s} index={i} currentUserId={user?.id} />)}
          </div>
        </div>
      )}

      <h2 className="font-heading font-extrabold text-xl text-[#1A1D20] mb-3">Reviews</h2>
      {data.reviews.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 text-center text-[#9CA3AF]">No reviews yet.</div>
      ) : (
        <div className="space-y-3">
          {data.reviews.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
              <div className="flex items-center justify-between mb-1">
                <span className="font-bold text-[#1A1D20]">{r.reviewer_name}</span>
                <StarRating value={r.rating} size={14} />
              </div>
              {r.comment && <p className="text-sm text-[#4A5361]">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
